<script lang="ts">
  import FormErrors from "./FormErrors.svelte";
  import RequiredFieldError from "@shared/svelte/Components/RequiredFieldError.svelte";
  import ProductImage from "./ProductImage.svelte";
  import fileUtils from "../../lib/file_from_drop";

  import type { Errors } from "@shared/types/Errors";
  import type { ImageUploadRes, Dimensions } from "@shared/types/Images";
  import type { MediaObj } from "@shared/types/Media";

  export let entityType: string;
  export let obj: MediaObj = null;
  export let showPreview: boolean = true;
  export let propertyOverride: string = null;
  export let postSaveCB: any = null;
  export let postRemoveCB: any = null;
  export let setLoading: any = null;
  export let dimensions: Dimensions = {};
  export let helperText: string | null = null;
  export let removeImageOverride: any = null;
  export let showErrors: boolean = true;
  export let allowVideo: boolean = false;
  export let required: boolean = false;
  export let errors: Errors = {
    messages: [],
  };

  $: inputEl = null;
  const uuid = Math.random().toString().slice(0, 7);

  let loading: boolean = false;

  let mode: string = obj?.cloudinary_image_url || obj?.cloudinary_video_url ? "show" : "edit";
  let fileData: any = null;

  const doSetLoading = (val: boolean) => {
    if (setLoading) {
      setLoading(val);
    }
    loading = val;
  };

  const prepareHelperText = () => {
    let s = "";
    if (dimensions.min_width && dimensions.min_height) {
      s += `Min image size: ${dimensions.min_width}x${dimensions.min_height}`;
    }
    if (dimensions.max_file_size) {
      s += `\nMax file size: ${dimensions.max_file_size}MB`;
    }
    return s;
  };

  // only implemented in distributor+admin app right now
  const remove = () => {
    if (removeImageOverride) {
      removeImageOverride();
      mode = "start";
      return;
    }

    if (!obj.image_upload_id) {
      return;
    }
    doSetLoading(true);

    window
      //@ts-ignore
      .$http({
        method: "POST",
        //@ts-ignore
        url: `/api/${window.C.APP_NAME}/image_uploads/${obj.image_upload_id}/remove_image_reference`,
        data: {
          image_upload: {
            ...obj,
            entity_id: obj?.id,
            image_upload_id_property_override: propertyOverride,
            entity_type: entityType,
          },
        },
      })
      .then(() => {
        loading = false;
        if (postRemoveCB) {
          postRemoveCB();
        }
        mode = "start";
      })
      .catch(res => {
        loading = false;
        console.error(res);
      });
  };

  const saveImage = () => {
    const formData: FormData = new FormData();
    formData.append("image_upload[file]", fileData);
    formData.append("image_upload[entity_type]", entityType);

    if (obj.id) {
      formData.append("image_upload[entity_id]", obj.id.toString());
    }

    if (propertyOverride) {
      formData.append("image_upload[image_upload_id_property_override]", propertyOverride);
    }
    Object.keys(dimensions).forEach(function (dim) {
      if (dim && dimensions[dim]) {
        formData.append(`image_upload[${dim}]`, dimensions[dim]);
      }
    });

    window
      //@ts-ignore
      .$http({
        method: "POST",
        //@ts-ignore
        url: `/api/${window.C.APP_NAME}/image_uploads`,
        headers: {
          "Content-Type": undefined,
        },
        data: formData,
      })
      .then((res: ImageUploadRes) => {
        loading = false;
        fileData = null;
        obj.cloudinary_image_url = res.data.cloudinary_image_url;
        obj.image_upload_id = res.data.id;
        mode = "show";
        if (res.data.dimensions_message) {
          errors = { messages: [res.data.dimensions_message] };
        }
        if (postSaveCB) {
          postSaveCB(obj);
        }
      })
      .catch(handleError);
  };

  const handleError = res => {
    doSetLoading(false);
    loading = false;
    if (res.data?.errors?.messages) {
      errors = res.data.errors;
    } else if (res.status >= 500) {
      errors = { messages: ["Server error"] };
    } else {
      errors = { messages: ["Unknown error"] };
    }
  };

  const saveVideo = () => {
    const formData: FormData = new FormData();
    formData.append("video_upload[file]", fileData);
    formData.append("video_upload[entity_type]", entityType);

    window
      //@ts-ignore
      .$http({
        method: "POST",
        //@ts-ignore
        url: `/api/admin/video_uploads`,
        headers: {
          "Content-Type": undefined,
        },
        data: formData,
      })
      .then(res => {
        loading = false;
        fileData = null;

        obj.cloudinary_video_url = res.data.cloudinary_video_url;
        obj.video_upload_id = res.data.id;

        mode = "show";

        if (postSaveCB) {
          postSaveCB(obj);
        }
      })
      .catch(handleError);
  };

  const save = () => {
    if (!fileData) {
      return (loading = false);
    }
    doSetLoading(true);

    if (fileData.type.includes("video") && allowVideo) {
      saveVideo();
    } else {
      saveImage();
    }
  };

  let dragging = false;
  const startDragging = () => {
    dragging = true;
  };
  const stopDragging = () => {
    dragging = false;
  };

  const uploadFromDrop = event => {
    stopDragging();
    fileData = fileUtils.getFilesFromDropEvent(event)[0];
    save();
  };

  const uploadFromBrowse = event => {
    stopDragging();
    fileData = event.target.files[0];
    save();
  };

  const imageRequiredText: string = `Please upload an image${allowVideo ? " or video" : ""}`;
  let showImageRequiredText: boolean;
  const mouseLeaveCallback = () => {
    if (required && !obj.image_upload_id) {
      showImageRequiredText = true;
    } else {
      showImageRequiredText = false;
    }
  };

</script>

<style>
  input {
    position: absolute !important;
    height: 1px;
    width: 1px;
    overflow: hidden;
    clip: rect(1px 1px 1px 1px);
    clip: rect(1px, 1px, 1px, 1px);
    white-space: nowrap;
  }
  #drop-target {
    padding: 20px;
    border: 1px dashed gray;
    width: inherit;
  }
  #drop-target.dragging {
    background-color: lightblue;
    border: 1px dashed gray;
  }

</style>

{#if showErrors}
  <FormErrors {errors} extraAttrs={{ style: "top: 120px;" }} />
{/if}
{#if loading}
  Loading...
{:else if mode == "show"}
  <figure class="fizz-stack-16">
    {#if showPreview && entityType == "ActionBarData"}
      <div class="fizz-stack-16 fizz-text-center">
        <ProductImage
          product={obj}
          fizz={true}
          className="noop"
          width={dimensions.max_width}
          height={dimensions.max_height}
        />
      </div>
    {:else if showPreview}
      <div class="fizz-image-container fizz-stack-16" style="--height: 85px;">
        <ProductImage product={obj} fizz={true} className="noop" />
      </div>
    {/if}
    {#if obj?.cloudinary_image_url || obj?.cloudinary_video_url}
      <figcaption class="fizz-flex-row">
        <button
          class="fizz-button fizz-button-small fizz-flex-item fizz-inline-block"
          disabled={loading}
          on:click|preventDefault={remove}
        >
          Remove
        </button>
      </figcaption>
    {/if}
  </figure>
{:else if mode == "edit" || mode == "start"}
  <label
    id="drop-target-{obj.id}-{uuid}"
    for="file-upload-header-image-{obj.id}-{uuid}"
    class="fizz-file-upload fizz-stack-8 cursor-pointer"
    class:dragging
    on:drop|preventDefault={uploadFromDrop}
    on:dragover|preventDefault={startDragging}
    on:dragleave|preventDefault={stopDragging}
    on:mouseleave={mouseLeaveCallback}
  >
    <input
      type="file"
      data-testid="image-upload-widget-input"
      on:change={uploadFromBrowse}
      id="file-upload-header-image-{obj.id}-{uuid}"
      bind:this={inputEl}
    />
    <span>{dragging ? "Drop" : "Click"} to upload</span>
  </label>
  {#if showImageRequiredText}
    <RequiredFieldError errorText={imageRequiredText} />
  {/if}
  <p class="fizz-text-small-subdued fizz-stack-16" style="white-space: pre-line">
    {helperText || prepareHelperText()}
  </p>
{/if}
